// «Cómo se reparte el plan en tus líneas» (tanda «grupos, dinero y DV360», contrato §6 y §9.2). Cada
// grupo del plan (la columna «Grupo» de la planilla) va a una línea de seguimiento, a una línea nueva
// o a «No seguir desde el plan», y varios grupos pueden ir a la misma línea. El reparto se guarda por
// conexión y sirve para los meses siguientes. Viaja en `proposal.group_mapping`:
//   {groups: [{key, label, table_index, amount, total_source, cells, platforms, saved, suggested,
//              is_new}], lines: [{bu_id, name, allocated_budget, currency}], pending}
// Sin la clave (backend viejo o agencia sin el flag) no hay bloque. Módulo puro para testearlo sin
// montar la vista.
//
// Ronda 2 (R9, contrato §13): cada grupo trae además `implicit: {target: 'SECTION', section_key, lines:
// [{name, amount}]}`, que es a dónde va hoy (la línea de su sección), y `split`. «Como hoy» es un destino
// más (`SECTION`) y se guarda como cualquier otro. Un grupo `split` se reparte por plataforma: no se elige
// ni viaja. Y `group_mapping.suggested_notice` dice que hay sugerencias que lo cambian. Sin esas claves
// (backend de la ronda 1), todo se ve como antes.

export const GROUP_MAPPING_PIN_KEY = 'group_mapping';
export const TARGET_BU = 'BU';
export const TARGET_NEW = 'NEW';
export const TARGET_UNTRACKED = 'UNTRACKED';
export const TARGET_SECTION = 'SECTION';
// El mismo `BusinessUnit.NAME_MAX_LENGTH` del backend que usa el nombre de una línea del plan.
export const NEW_LINE_NAME_MAX_LENGTH = 100;

// `SIBLINGS` (ronda 2b): «No seguir desde el plan» para el grupo sin sugerencia de una sección donde los otros
// grupos sí la tienen (ninguna línea del cliente lo incluye hoy).
const REASON_KEYS = {PIN: 'suggested_pin', AMOUNT: 'suggested_amount', NAME: 'suggested_name',
                     SIBLINGS: 'suggested_siblings'};
// Las razones que cambian la propuesta de hoy (las que prenden `suggested_notice`). `PIN` es la línea de hoy.
const CHANGING_REASONS = new Set(['AMOUNT', 'NAME', 'SIBLINGS']);
const SUBTOTAL_SOURCES = new Set(['SUBTOTAL_FORMULA', 'SUBTOTAL_SUM_MATCH']);

// La duda no bloqueante de grupos nuevos se responde en el bloque, no en la lista de dudas.
export function isGroupMappingQuestion(item) {
    return ((item && item.question) || {}).profile_pin_key === GROUP_MAPPING_PIN_KEY;
}

export function groupMappingState(proposal) {
    const raw = (proposal && proposal.group_mapping) || {};
    const groups = Array.isArray(raw.groups) ? raw.groups.filter((g) => g && g.key) : [];
    const lines = Array.isArray(raw.lines) ? raw.lines.filter(Boolean) : [];
    // Plan parcial (contrato §6.4): tablas cuyo total declarado supera la suma de sus grupos, con esa
    // diferencia (`declared_rest`). Sin la clave (backend anterior), ninguna.
    const tables = Array.isArray(raw.tables) ? raw.tables.filter((tb) => tb && Number(tb.declared_rest) > 0) : [];
    const hasGroupMapping = groups.length > 0;
    const choosable = groups.filter((g) => !isSplitGroup(g));
    return {
        hasGroupMapping, groups, lines, tables,
        pending: hasGroupMapping && !!raw.pending,
        // Sugerencias que cambian la propuesta de hoy: se pre-eligen y se avisa (en azul) que hay que guardar.
        suggestedNotice: hasGroupMapping && !!raw.suggested_notice,
        // «Mientras no guardes…»: siempre que haya algo que elegir con «como hoy» (padre, review C-mp-fe-02). Es
        // cierto con sugerencias pre-elegidas o sin ellas, y dice qué hace «Guardar reparto».
        implicitHint: choosable.some(offersSection),
        hasChoosable: choosable.length > 0,
    };
}

// Un grupo de una sección que se reparte por plataforma (D24): no tiene select ni viaja al guardar
// (el backend lo rechaza).
export function isSplitGroup(group) {
    return !!(group && group.split);
}

// Las líneas a las que va el grupo hoy (`implicit.lines`).
export function implicitLineNames(group) {
    const implicit = (group || {}).implicit;
    const lines = implicit && Array.isArray(implicit.lines) ? implicit.lines : [];
    return lines.map((line) => String((line || {}).name || '').trim()).filter(Boolean);
}

// Una sección que hoy nadie sigue (contrato §13, caso 8570: una tabla que el LLM no leyó): su «como hoy» es
// `implicit: {target: 'UNTRACKED', lines: []}`, «Como hoy: no se sigue». Guardarla «como hoy» sigue siendo SECTION.
export function isImplicitUntracked(group) {
    return !isSplitGroup(group) && ((group || {}).implicit || {}).target === TARGET_UNTRACKED;
}

// ¿El select ofrece «Como hoy: «X»»? Con `implicit` (backend de la ronda 2) o con un «como hoy» ya
// guardado. Sin nombres se dice «Como hoy» (`implicit_as_today_unnamed`).
export function offersSection(group) {
    if (!group || isSplitGroup(group)) return false;
    return !!group.implicit || (group.saved || {}).target === TARGET_SECTION;
}

function lineOf(lines, buId) {
    if (buId === null || buId === undefined) return null;
    return (lines || []).find((line) => String(line.bu_id) === String(buId)) || null;
}

// Un destino del backend (`saved` / `suggested`) como elección de la pantalla. Una línea que ya no
// está entre las del cliente (se dio de baja) no se pre-elige: el select quedaría mudo.
export function normalizeChoice(choice, lines) {
    if (!choice || !choice.target) return null;
    if (choice.target === TARGET_BU) {
        const line = lineOf(lines, choice.bu_id);
        return line ? {target: TARGET_BU, bu_id: line.bu_id} : null;
    }
    if (choice.target === TARGET_NEW) return {target: TARGET_NEW, name: choice.name || ''};
    if (choice.target === TARGET_UNTRACKED) return {target: TARGET_UNTRACKED};
    if (choice.target === TARGET_SECTION) return {target: TARGET_SECTION};
    return null;
}

// Una elección del grupo: «como hoy» sólo si el grupo lo ofrece.
function groupChoice(group, choice, lines) {
    const normalized = normalizeChoice(choice, lines);
    if (normalized && normalized.target === TARGET_SECTION && !offersSection(group)) return null;
    return normalized;
}

// Lo que el grupo hace hoy: lo guardado o, sin guardar, «como hoy» (si el backend lo manda). Es la vara de
// «Guardar reparto»: se guarda si algo queda distinto.
export function currentChoice(group, lines) {
    if (isSplitGroup(group)) return null;
    return groupChoice(group, (group || {}).saved, lines) || (offersSection(group) ? {target: TARGET_SECTION} : null);
}

// ¿La sugerencia manda el grupo a otra línea que la de hoy? El mismo criterio que `_suggestion_differs` del
// backend, que es el que prende `suggested_notice`: por monto, por nombre o por sus vecinos (una `PIN` apunta
// a la línea de hoy). La línea de hoy es `implicit.bu_id` (la del pin o la del mismo nombre; `null` si hoy se
// crearía, que difiere de toda línea) y, si el backend no lo manda, la que se llama como la línea de la
// sección. Si hoy la sección no se sigue, difiere todo lo que no sea «No seguir».
export function suggestionDiffersFromToday(group, lines) {
    const suggested = (group || {}).suggested || {};
    if (!CHANGING_REASONS.has(suggested.reason)) return false;
    if (isImplicitUntracked(group)) return suggested.target !== TARGET_UNTRACKED;
    const implicit = (group || {}).implicit || {};
    const todayName = normalizeLineName(implicitLineNames(group)[0]);
    if (suggested.target === TARGET_BU) {
        if (Object.prototype.hasOwnProperty.call(implicit, 'bu_id')) {
            return String(implicit.bu_id) !== String(suggested.bu_id);
        }
        const line = lineOf(lines, suggested.bu_id);
        return !line || normalizeLineName(line.name) !== todayName;
    }
    if (suggested.target === TARGET_NEW) return normalizeLineName(suggested.name) !== todayName;
    return suggested.target === TARGET_UNTRACKED;
}

// Pre-elegido: lo guardado y, si no hay, lo sugerido; sin ninguno, «como hoy» o, con el backend de la
// ronda 1, «Elegir…». Con «como hoy» disponible, la sugerencia se pre-elige sólo si el backend avisa que
// hay sugerencias que cambian la propuesta (`suggestedFirst` = `suggested_notice`) y si ÉSTA la cambia: una
// que apunta a la línea de hoy («ya estaba vinculada», o por monto a la misma línea) escondería el «como
// hoy», sumaría en el pie un grupo de la sección y no los otros, y se guardaría atada a esa línea.
export function initialChoice(group, lines, suggestedFirst = false) {
    if (isSplitGroup(group)) return null;
    const saved = groupChoice(group, (group || {}).saved, lines);
    if (saved) return saved;
    if (!offersSection(group) || (suggestedFirst && suggestionDiffersFromToday(group, lines))) {
        const suggested = groupChoice(group, (group || {}).suggested, lines);
        if (suggested) return suggested;
    }
    return currentChoice(group, lines);
}

// La elección vigente de cada grupo: lo que tocó el usuario (`edits`; null = eligió «Elegir…») o lo
// pre-elegido. Lo tocado también pasa por `normalizeChoice`: si una regeneración saca de `lines` la línea
// elegida, la elección vuelve a lo pre-elegido en vez de quedar apuntando a una línea que no está. Un
// grupo split no tiene elección.
export function effectiveChoices(groups, lines, edits = {}, suggestedFirst = false) {
    const choices = {};
    (groups || []).forEach((group) => {
        if (isSplitGroup(group)) {
            choices[group.key] = null;
            return;
        }
        const touched = Object.prototype.hasOwnProperty.call(edits, group.key);
        if (touched && edits[group.key] === null) choices[group.key] = null;
        else {
            choices[group.key] = (touched && groupChoice(group, edits[group.key], lines))
                || initialChoice(group, lines, suggestedFirst);
        }
    });
    return choices;
}

// ¿Se ofrece «Elegir…»? Sólo si el grupo no tiene un reparto guardado (guardar mergea y no borra, así que
// «Elegir…» sobre uno guardado prometería algo que no pasa) ni «como hoy», que ocupa ese lugar. Una
// sugerencia pre-elegida sí se puede dejar sin decidir.
export function canLeaveUndecided(group, lines) {
    return !offersSection(group) && !groupChoice(group, (group || {}).saved, lines);
}

// Valor del <select> de un grupo.
export function choiceValue(choice) {
    if (!choice) return '';
    if (choice.target === TARGET_BU) return `bu:${choice.bu_id}`;
    if (choice.target === TARGET_NEW) return 'new';
    if (choice.target === TARGET_UNTRACKED) return 'untracked';
    if (choice.target === TARGET_SECTION) return 'section';
    return '';
}

// Y la vuelta. Pasar a «Línea nueva…» conserva el nombre si ya se había escrito uno.
export function choiceFromValue(value, lines, previous = null) {
    if (typeof value === 'string' && value.startsWith('bu:')) {
        const line = lineOf(lines, value.slice(3));
        return line ? {target: TARGET_BU, bu_id: line.bu_id} : null;
    }
    if (value === 'new') {
        return {target: TARGET_NEW, name: previous && previous.target === TARGET_NEW ? previous.name || '' : ''};
    }
    if (value === 'untracked') return {target: TARGET_UNTRACKED};
    if (value === 'section') return {target: TARGET_SECTION};
    return null;
}

// La misma normalización que `names.normalize_name` del backend (NFC, sin espacios de más, sin
// mayúsculas): es con la que decide que el nombre de una «Línea nueva…» ya existe.
export function normalizeLineName(name) {
    return String(name || '').normalize('NFC').trim().split(/\s+/).join(' ').toLowerCase();
}

// ¿La «Línea nueva…» se llama igual que una línea activa del cliente? El backend la rechaza
// (`INVALID_GROUP_MAPPING`): se avisa antes de guardar, para que elija esa línea en la lista. Salvo que sea
// la misma «Línea nueva…» que ya estaba guardada (`saved`, comparada como el backend: el nombre sin espacios
// en los bordes, exacto): cuando el apply la crea, su nombre pasa a ser el de una línea activa, y volver a
// guardarla sin cambios no es un error.
export function newNameTaken(choice, lines, saved = null) {
    if (!choice || choice.target !== TARGET_NEW) return false;
    if (saved && saved.target === TARGET_NEW && saved.name === String(choice.name || '').trim()) return false;
    const name = normalizeLineName(choice.name);
    return !!name && (lines || []).some((line) => normalizeLineName(line.name) === name);
}

export function isValidChoice(choice) {
    if (!choice) return false;
    if (choice.target === TARGET_BU) return choice.bu_id !== null && choice.bu_id !== undefined;
    if (choice.target === TARGET_NEW) return !!String(choice.name || '').trim();
    return choice.target === TARGET_UNTRACKED || choice.target === TARGET_SECTION;
}

export function sameChoice(a, b) {
    if (!a || !b || a.target !== b.target) return !a && !b;
    if (a.target === TARGET_BU) return String(a.bu_id) === String(b.bu_id);
    if (a.target === TARGET_NEW) {
        return String(a.name || '').trim().toLowerCase() === String(b.name || '').trim().toLowerCase();
    }
    return true;
}

// La razón de la sugerencia se dice sólo mientras la elección ES la sugerida y no hay una guardada:
// lo guardado es una decisión del usuario y no necesita justificarse.
export function suggestionReasonKey(group, choice, lines) {
    if (normalizeChoice((group || {}).saved, lines)) return null;
    const suggested = normalizeChoice((group || {}).suggested, lines);
    if (!suggested || !sameChoice(suggested, choice)) return null;
    return REASON_KEYS[(group.suggested || {}).reason] || null;
}

// De dónde sale el monto del grupo (decisión A): el subtotal de la planilla o la suma de sus filas.
// Devuelve la clave relativa a `media_plan.group_mapping` y sus valores, o null. Una sola fila no
// lleva nota: el monto ES esa celda. Con un subtotal, `cells` son las celdas de DETALLE del grupo: la
// del subtotal viaja aparte, en `subtotal_cell` (pedido a `mp-be-reparto`); sin ella no se nombra
// ninguna celda, para no señalar una fila como si fuera el subtotal.
export function amountSource(group) {
    const cells = ((group || {}).cells || []).filter(Boolean);
    if (SUBTOTAL_SOURCES.has((group || {}).total_source)) {
        const cell = (group || {}).subtotal_cell;
        return cell ? {key: 'amount_subtotal', values: {cell}} : null;
    }
    if ((group || {}).total_source === 'SUM_OF_ROWS' && cells.length >= 2) {
        return {key: 'amount_sum_of_rows', values: {count: cells.length}};
    }
    return null;
}

const amountOf = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

// Las plataformas del grupo que no se siguen (sin canal en Dashbo): su monto no va a la línea
// (decisión B).
export function untrackedPlatforms(group) {
    return ((group || {}).platforms || []).filter((p) => p && p.tracked === false);
}

export function trackedAmount(group) {
    const untracked = untrackedPlatforms(group).reduce((sum, p) => sum + amountOf(p.amount), 0);
    return amountOf((group || {}).amount) - untracked;
}

// Clave del destino de una elección: `bu:<id>`, `new:<nombre>`, 'untracked', 'section', o null si no hay
// una elección válida.
function destinationKey(choice) {
    if (!isValidChoice(choice)) return null;
    if (choice.target === TARGET_UNTRACKED) return 'untracked';
    if (choice.target === TARGET_SECTION) return 'section';
    return choice.target === TARGET_BU ? `bu:${choice.bu_id}` : `new:${String(choice.name).trim().toLowerCase()}`;
}

// Lo que recibe cada línea con las elecciones de la pantalla, en el orden en que aparecen los grupos,
// y lo que queda sin seguir (grupos «No seguir desde el plan» + plataformas sin canal).
//
// `tables` (plan parcial): el resto declarado de una tabla va a su destino sólo si TODOS sus grupos van
// al mismo destino seguido, la misma regla del backend. Si van a destinos distintos lo decide la duda del
// resto, y si alguno está sin decidir o todos son «No seguir», no se suma.
//
// «Como hoy» no suma acá: su monto (`implicit.lines[].amount`) es el de la línea de la SECCIÓN entera, que
// comparten todos sus grupos y cambia si se reparte uno solo. Sumarlo daría números falsos. Salvo el «como
// hoy» de una sección que hoy no se sigue: es «No seguir» con el monto del propio grupo.
export function lineTotals(groups, choices, lines, tables = []) {
    const destinations = [];
    const byKey = {};
    let untracked = 0;
    (groups || []).forEach((group) => {
        const choice = (choices || {})[group.key];
        if (!isValidChoice(choice)) return;
        if (choice.target === TARGET_SECTION) {
            if (isImplicitUntracked(group)) untracked += amountOf(group.amount);
            return;
        }
        if (choice.target === TARGET_UNTRACKED) {
            untracked += amountOf(group.amount);
            return;
        }
        untracked += amountOf(group.amount) - trackedAmount(group);
        const isLine = choice.target === TARGET_BU;
        const key = destinationKey(choice);
        if (!byKey[key]) {
            const line = isLine ? lineOf(lines, choice.bu_id) : null;
            byKey[key] = {key, name: isLine ? (line || {}).name || '' : String(choice.name).trim(),
                          isNew: !isLine, amount: 0};
            destinations.push(byKey[key]);
        }
        byKey[key].amount += trackedAmount(group);
    });
    (tables || []).forEach((table) => {
        const rest = amountOf(table.declared_rest);
        const tableGroups = (groups || []).filter((g) => Number(g.table_index) === Number(table.index));
        if (rest <= 0 || tableGroups.length === 0) return;
        const keys = tableGroups.map((g) => destinationKey((choices || {})[g.key]));
        const single = new Set(keys).size === 1 ? keys[0] : null;
        if (single && single !== 'untracked' && byKey[single]) byKey[single].amount += rest;
    });
    return {destinations, untracked};
}

// Lo que se guarda: TODOS los grupos visibles que tienen destino (también los pre-elegidos que el
// usuario no tocó, que es lo que confirma la sugerencia, y los que quedan «como hoy», que viajan como
// `SECTION` para que un reparto parcial no deje grupos huérfanos). Un grupo en «Elegir…» no viaja: el
// backend mergea con lo guardado y lo sigue preguntando. Un grupo split, tampoco.
export function mappingPayload(groups, choices) {
    const mapping = {};
    (groups || []).forEach((group) => {
        const choice = (choices || {})[group.key];
        if (isSplitGroup(group) || !isValidChoice(choice)) return;
        if (choice.target === TARGET_BU) mapping[group.key] = {target: TARGET_BU, bu_id: choice.bu_id};
        else if (choice.target === TARGET_NEW) {
            mapping[group.key] = {target: TARGET_NEW,
                                  name: String(choice.name).trim().slice(0, NEW_LINE_NAME_MAX_LENGTH)};
        } else if (choice.target === TARGET_SECTION) mapping[group.key] = {target: TARGET_SECTION};
        else mapping[group.key] = {target: TARGET_UNTRACKED};
    });
    return mapping;
}

// «Seguir como hoy» (ronda 2b): descartar las sugerencias es guardar TODOS los grupos visibles no split
// «como hoy». Con `suggested_notice` el backend no propone la línea de hoy de esas secciones hasta que se
// guarde o se descarte el reparto.
export function dismissPayload(groups) {
    const mapping = {};
    (groups || []).forEach((group) => {
        if (group && group.key && !isSplitGroup(group)) mapping[group.key] = {target: TARGET_SECTION};
    });
    return mapping;
}

// ¿Se puede guardar? Hace falta al menos un destino, ninguna «Línea nueva…» sin nombre o con el de una
// línea que ya existe, y algún grupo distinto de lo que hace hoy (`currentChoice`: lo guardado o, sin
// guardar, «como hoy»). Si todo queda igual, guardar no cambia la propuesta. Salvo un grupo NUEVO (sin
// `saved` en una conexión que ya tiene reparto): guardarlo, aunque sea «como hoy», es lo que responde su
// duda, así que cuenta como cambio.
export function canSaveMapping(groups, choices, lines) {
    const list = (groups || []).filter((g) => !isSplitGroup(g));
    const hasSaved = list.some((g) => g.saved && g.saved.target);
    const badNew = list.some((g) => {
        const choice = (choices || {})[g.key];
        return choice && choice.target === TARGET_NEW
            && (!isValidChoice(choice) || newNameTaken(choice, lines, g.saved));
    });
    if (badNew) return false;
    const valid = list.filter((g) => isValidChoice((choices || {})[g.key]));
    if (valid.length === 0) return false;
    return valid.some((g) => (hasSaved && !(g.saved && g.saved.target))
        || !sameChoice(currentChoice(g, lines), choices[g.key]));
}
